import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Users, Package, ShoppingBag, Flag, BadgeCheck, Rocket, Shield, Trash2, Ban, Truck,
  ExternalLink, AlertTriangle, ShieldCheck, LifeBuoy, Check, LayoutDashboard, Settings,
  MessageSquareWarning, Euro, TrendingUp, Search, Eye, Store, Repeat, CalendarClock, X, MapPin, ArrowDownToLine, Mail, Send, CheckCircle2, XCircle, Ticket, Plus, Power, CreditCard,
} from "lucide-react";
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import api, { fileUrl } from "../lib/api";
import { ISLAND_NAME, courierLink, courierLabel } from "../lib/data";
import { formatPrice, formatDate, timeAgo } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import DateRangePicker from "../components/DateRangePicker";
import { toast } from "sonner";

const ORDER_COLORS = { to_ship: "bg-amber-100 text-amber-700", shipped: "bg-blue-100 text-blue-700", delivered: "bg-teal-100 text-teal-700", completed: "bg-emerald-100 text-emerald-700", refunded: "bg-slate-200 text-slate-600" };

const fmtDay = (d) => { const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };

function Kpi({ icon: Icon, label, value, color, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={!onClick}
      className={`text-left bg-white border border-slate-200 rounded-2xl p-4 transition-all ${onClick ? "hover:border-[#008080] hover:shadow-md cursor-pointer" : "cursor-default"}`}>
      <span className={`h-9 w-9 grid place-items-center rounded-xl ${color} mb-2`}><Icon className="h-4.5 w-4.5" /></span>
      <p className="font-display font-extrabold text-2xl">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </button>
  );
}

function AdminOrderCard({ order, t, lang, onResolve, showResolve }) {
  const link = courierLink(order.carrier, order.tracking_code);
  const d = order.dispute;
  return (
    <div data-testid={`admin-order-${order.id}`} className={`bg-white border rounded-2xl p-4 ${showResolve ? "border-rose-200" : "border-slate-200"}`}>
      <div className="flex items-center gap-3">
        <Link to={`/listing/${order.listing_id}`} className="h-14 w-14 rounded-xl overflow-hidden bg-slate-100 shrink-0">
          {order.listing_photo && <img src={fileUrl(order.listing_photo)} alt="" className="w-full h-full object-cover" />}
        </Link>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{order.listing_title}</p>
          <p className="text-xs text-slate-400 truncate">{order.buyer?.name} → {order.seller?.name} · {formatPrice(order.total ?? order.amount, lang)}</p>
          {order.commission != null && <p className="text-[11px] text-emerald-600">{t("apanel.commission")}: {formatPrice(order.commission, lang)}</p>}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${ORDER_COLORS[order.status]}`}>{t(`orders.statuses.${order.status}`)}</span>
          <span className="text-[10px] text-slate-400">{t(`orders.escrowStates.${order.escrow}`)}</span>
        </div>
      </div>
      {order.carrier ? (
        <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm space-y-1">
          <p className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1"><Truck className="h-3.5 w-3.5" />{t("admin.trackingProof")}</p>
          <div className="flex justify-between"><span className="text-slate-500">{t("orders.carrier")}</span><span className="font-medium">{courierLabel(order)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">{t("orders.trackingCode")}</span><span className="font-mono-badge">{order.tracking_code}</span></div>
          {link && <a href={link} target="_blank" rel="noopener noreferrer" data-testid={`admin-track-link-${order.id}`} className="inline-flex items-center gap-1 text-[#008080] font-medium hover:underline"><ExternalLink className="h-3.5 w-3.5" />{t("orders.trackPackage")}</a>}
        </div>
      ) : <p className="mt-3 text-xs text-slate-400">{t("orders.awaitingShip")}</p>}
      {d && (
        <div className="mt-3 rounded-xl bg-rose-50 border border-rose-200 p-3 text-sm">
          <p className="text-xs font-semibold text-rose-500 uppercase flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />{t("admin.disputeReason")}</p>
          <p className="text-rose-700 mt-0.5">{t(`dispute.reasons.${d.reason}`)}</p>
          {d.comment && <p className="text-slate-600 mt-1">"{d.comment}"</p>}
        </div>
      )}
      {showResolve && (
        <div className="flex flex-wrap gap-2 mt-3">
          <Button data-testid={`resolve-release-${order.id}`} onClick={() => onResolve(order, "release")} className="h-9 bg-emerald-500 hover:bg-emerald-600"><ShieldCheck className="h-4 w-4 mr-1" />{t("admin.releaseFunds")}</Button>
          <Button data-testid={`resolve-refund-${order.id}`} onClick={() => onResolve(order, "refund")} variant="outline" className="h-9 text-rose-600 border-rose-200 hover:bg-rose-50">{t("admin.refundBuyer")}</Button>
        </div>
      )}
    </div>
  );
}

const SECTIONS = [
  { id: "dashboard", icon: LayoutDashboard },
  { id: "tickets", icon: LifeBuoy },
  { id: "moderation", icon: MessageSquareWarning },
  { id: "config", icon: Settings },
];

function CompletedOrdersModal({ open, onOpenChange, orders, t, lang }) {
  const [expanded, setExpanded] = useState(null);
  const completed = (orders || []).filter((o) => o.status === "completed");
  const total = completed.reduce((s, o) => s + (o.total ?? o.amount ?? 0), 0);
  const shortId = (id) => "#" + String(id).slice(-8).toUpperCase();
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setExpanded(null); onOpenChange(o); }}>
      <DialogContent className="rounded-2xl max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="gmv-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Euro className="h-5 w-5 text-emerald-600" />{t("apanel.gmv")}</DialogTitle>
          <DialogDescription>{t("apanel.completedOrders")}</DialogDescription>
        </DialogHeader>
        <div className="flex items-baseline justify-between rounded-2xl bg-emerald-50 border border-emerald-100 px-4 py-3">
          <span className="text-sm text-slate-500">{t("apanel.completedOrders")}: <b className="text-slate-700">{completed.length}</b></span>
          <span data-testid="gmv-modal-total" className="font-display font-extrabold text-2xl text-emerald-700">{formatPrice(total, lang)}</span>
        </div>
        {completed.length === 0 ? (
          <p className="text-center py-10 text-slate-400">{t("admin.noOrders")}</p>
        ) : (
          <div className="mt-3 space-y-2">
            {completed.map((o) => (
              <div key={o.id} data-testid={`gmv-row-${o.id}`} className="border border-slate-200 rounded-2xl overflow-hidden">
                <div className="flex items-center gap-3 p-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-xs font-semibold text-slate-700">{shortId(o.id)}</p>
                    <p className="text-xs text-slate-400 truncate">{formatDate(o.completed_at || o.created_at, lang)} · {o.listing_title}</p>
                  </div>
                  <span className="font-semibold text-sm text-emerald-700 shrink-0">{formatPrice(o.total ?? o.amount, lang)}</span>
                  <button data-testid={`gmv-history-${o.id}`} onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                    className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-[#008080] hover:underline">
                    <Eye className="h-3.5 w-3.5" />{t("admin.viewHistory")}
                  </button>
                </div>
                {expanded === o.id && (
                  <div className="p-3 pt-0"><AdminOrderCard order={o} t={t} lang={lang} onResolve={() => {}} showResolve={false} /></div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ActiveSubsModal({ open, onOpenChange, business, t, lang }) {
  const navigate = useNavigate();
  const isActive = (b) => b.is_professional && (!b.subscription_current_period_end || new Date(b.subscription_current_period_end) > new Date());
  const subs = (business || []).filter(isActive);
  // Mobile: the hardware/browser back button closes the modal (returns to the previous screen).
  useEffect(() => {
    if (!open) return;
    window.history.pushState({ cmModal: "subs" }, "");
    const onPop = () => onOpenChange(false);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [open, onOpenChange]);
  const openProfile = (id) => { onOpenChange(false); navigate(`/user/${id}`); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="subs-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Repeat className="h-5 w-5 text-teal-600" />{t("apanel.activeSubsTitle")}</DialogTitle>
          <DialogDescription>{t("apanel.activeSubsDesc")}</DialogDescription>
        </DialogHeader>
        <div className="flex items-baseline justify-between rounded-2xl bg-teal-50 border border-teal-100 px-4 py-3">
          <span className="text-sm text-slate-500">{t("apanel.activeSubs")}: <b className="text-slate-700">{subs.length}</b></span>
          <span data-testid="subs-modal-mrr" className="font-display font-extrabold text-2xl text-teal-700">{formatPrice(subs.length * 49, lang)}</span>
        </div>
        {subs.length === 0 ? (
          <p className="text-center py-10 text-slate-400" data-testid="subs-empty">{t("apanel.noActiveSubs")}</p>
        ) : (
          <div className="mt-3 space-y-2">
            {subs.map((b) => (
              <button key={b.id} data-testid={`sub-row-${b.id}`} onClick={() => openProfile(b.id)}
                className="w-full text-left flex items-center gap-3 border border-slate-200 rounded-2xl p-3 hover:border-[#008080] transition-colors">
                <span className="h-10 w-10 grid place-items-center rounded-xl bg-teal-100 text-teal-700 shrink-0"><Store className="h-4 w-4" /></span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{b.business_name || b.name}</p>
                  <p className="text-xs text-slate-400 truncate">{b.email}{b.subscription_current_period_end ? ` · ${t("apanel.renewsOn")} ${formatDate(b.subscription_current_period_end, lang)}` : ""}</p>
                </div>
                {b.cancel_at_period_end
                  ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 shrink-0">{t("apanel.canceling")}</span>
                  : <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 shrink-0">49 €</span>}
                <ExternalLink className="h-4 w-4 text-slate-300 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function BoostsModal({ open, onOpenChange, boosts, t, lang }) {
  const navigate = useNavigate();
  const items = boosts?.items || [];
  const total = boosts?.total ?? 0;
  // Mobile: the hardware/browser back button closes the modal (returns to the previous screen).
  useEffect(() => {
    if (!open) return;
    window.history.pushState({ cmModal: "boosts" }, "");
    const onPop = () => onOpenChange(false);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [open, onOpenChange]);
  const openListing = (b) => { if (!b.listing_exists) return; onOpenChange(false); navigate(`/listing/${b.listing_id}`); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="boosts-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Rocket className="h-5 w-5 text-amber-500" />{t("apanel.boostsTitle")}</DialogTitle>
          <DialogDescription>{t("apanel.boostsDesc")}</DialogDescription>
        </DialogHeader>
        <div className="flex items-baseline justify-between rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3">
          <span className="text-sm text-slate-500">{t("apanel.boostsCount")}: <b className="text-slate-700">{items.length}</b></span>
          <span data-testid="boosts-modal-total" className="font-display font-extrabold text-2xl text-amber-600">{formatPrice(total, lang)}</span>
        </div>
        {items.length === 0 ? (
          <p className="text-center py-10 text-slate-400" data-testid="boosts-empty">{t("apanel.noBoosts")}</p>
        ) : (
          <div className="mt-3 space-y-2">
            {items.map((b) => (
              <button key={b.id} data-testid={`boost-row-${b.id}`} onClick={() => openListing(b)} disabled={!b.listing_exists}
                className={`w-full text-left flex items-center gap-3 border border-slate-200 rounded-2xl p-3 transition-colors ${b.listing_exists ? "hover:border-[#008080]" : "opacity-60 cursor-default"}`}>
                <span className="h-10 w-10 grid place-items-center rounded-xl bg-amber-100 text-amber-600 shrink-0"><Rocket className="h-4 w-4" /></span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{b.listing_title}{b.listing_ref ? <span className="font-mono text-xs text-slate-400"> · {b.listing_ref}</span> : null}</p>
                  <p className="text-xs text-slate-400 truncate">{b.user_name} · {formatDate(b.created_at, lang)}{b.days ? ` · ${b.days}d` : ""}</p>
                </div>
                <span className="font-semibold text-sm text-amber-600 shrink-0">{formatPrice(b.amount, lang)}</span>
                {b.listing_exists && <ExternalLink className="h-4 w-4 text-slate-300 shrink-0" />}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function PendingPayoutsModal({ open, onOpenChange, data, t, lang, onUnlock }) {
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(null);
  const items = data?.items || [];
  const total = data?.total ?? 0;
  // Mobile: the hardware/browser back button closes the modal (returns to the previous screen).
  useEffect(() => {
    if (!open) return;
    window.history.pushState({ cmModal: "payouts" }, "");
    const onPop = () => onOpenChange(false);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [open, onOpenChange]);
  const unlock = async (o) => {
    setBusy(o.id);
    try { await onUnlock(o); } finally { setBusy(null); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setExpanded(null); onOpenChange(o); }}>
      <DialogContent className="rounded-2xl max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="payouts-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-purple-600" />{t("apanel.pendingPayoutTitle")}</DialogTitle>
          <DialogDescription>{t("apanel.pendingPayoutDesc")}</DialogDescription>
        </DialogHeader>
        <div className="flex items-baseline justify-between rounded-2xl bg-purple-50 border border-purple-100 px-4 py-3">
          <span className="text-sm text-slate-500">{t("apanel.pendingPayoutCount")}: <b className="text-slate-700">{items.length}</b></span>
          <span data-testid="payouts-modal-total" className="font-display font-extrabold text-2xl text-purple-700">{formatPrice(total, lang)}</span>
        </div>
        {items.length === 0 ? (
          <p className="text-center py-10 text-slate-400" data-testid="payouts-empty">{t("apanel.noPending")}</p>
        ) : (
          <div className="mt-3 space-y-2">
            {items.map((o) => (
              <div key={o.id} data-testid={`payout-row-${o.id}`} className="border border-slate-200 rounded-2xl overflow-hidden">
                <div className="flex items-center gap-3 p-3">
                  <button onClick={() => setExpanded(expanded === o.id ? null : o.id)} data-testid={`payout-toggle-${o.id}`}
                    className="flex-1 min-w-0 text-left">
                    <p className="font-medium text-sm truncate">{o.listing_title}</p>
                    <p className="text-xs text-slate-400 truncate">{o.seller?.name} · {formatDate(o.completed_at || o.created_at, lang)}</p>
                    <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full ${ORDER_COLORS[o.status]}`}>{t(`orders.statuses.${o.status}`)}</span>
                  </button>
                  <span className="font-semibold text-sm text-purple-700 shrink-0">{formatPrice(o.seller_net ?? o.total ?? o.amount, lang)}</span>
                  <Button data-testid={`unlock-payout-${o.id}`} onClick={() => unlock(o)} disabled={busy === o.id}
                    className="shrink-0 h-9 bg-emerald-500 hover:bg-emerald-600">
                    <ShieldCheck className="h-4 w-4 mr-1" />{busy === o.id ? t("common.loading") : t("apanel.unlockPayment")}
                  </Button>
                </div>
                {expanded === o.id && (
                  <div className="p-3 pt-0"><AdminOrderCard order={o} t={t} lang={lang} onResolve={() => {}} showResolve={false} /></div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function accountType(u, t) {
  if (u.role === "admin") return t("apanel.usersModal.typeAdmin");
  if (u.is_professional) return t("apanel.usersModal.typePro");
  return t("apanel.usersModal.typeParticular");
}

function UsersModal({ open, onOpenChange, users, t, lang, ban, unban, setSubscription }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [subShow, setSubShow] = useState(false);
  const [subPlan, setSubPlan] = useState("pro");
  const [subType, setSubType] = useState("months");
  const [subVal, setSubVal] = useState(1);
  const [subRevenue, setSubRevenue] = useState(true);
  const [subBusy, setSubBusy] = useState(false);
  const selectedRef = useRef(null);
  useEffect(() => { selectedRef.current = selected; }, [selected]);
  const PLAN_L = {
    current: lang === "it" ? "Piano attuale" : lang === "en" ? "Current plan" : "Plan actual",
    until: lang === "it" ? "Scadenza" : lang === "en" ? "Expires" : "Vence",
    activate: lang === "it" ? "Attiva abbonamento manuale" : lang === "en" ? "Activate manual subscription" : "Activar suscripción manual",
    plan: lang === "it" ? "Piano" : lang === "en" ? "Plan" : "Plan",
    duration: lang === "it" ? "Durata" : lang === "en" ? "Duration" : "Duración",
    months: lang === "it" ? "Mesi" : lang === "en" ? "Months" : "Meses",
    days: lang === "it" ? "Giorni" : lang === "en" ? "Days" : "Días",
    cancel: lang === "it" ? "Annulla" : lang === "en" ? "Cancel" : "Cancelar",
    confirm: lang === "it" ? "Attiva" : lang === "en" ? "Activate" : "Activar",
    revenueQ: lang === "it" ? "Conteggiare questo abbonamento nei ricavi?" : lang === "en" ? "Count this subscription in the revenue?" : "¿Contar esta suscripción en los ingresos?",
    revenueNote: lang === "it" ? "Attivazione manuale: decidi se includerla nei riquadri Ricavi Abbonamenti e Pacchetti attivi." : lang === "en" ? "Manual activation: choose whether to include it in the Subscription Revenue and Active Packages tiles." : "Activación manual: elige si incluirla en los paneles de Ingresos y Paquetes activos.",
    include: lang === "it" ? "Includi" : lang === "en" ? "Include" : "Incluir",
    exclude: lang === "it" ? "Escludi" : lang === "en" ? "Exclude" : "Excluir",
  };

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter((u) => (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s) || (u.nickname || "").toLowerCase().includes(s));
  }, [users, q]);

  // Mobile: browser/hardware back button steps back one screen (detail -> list -> closed).
  useEffect(() => {
    if (!open) return;
    setSelected(null); setQ("");
    window.history.pushState({ cmModal: "users" }, "");
    const onPop = () => { if (selectedRef.current) setSelected(null); else onOpenChange(false); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [open, onOpenChange]);

  // Keep the open detail card in sync with fresh data after ban/unban reloads.
  useEffect(() => {
    if (selected) { const fresh = users.find((u) => u.id === selected.id); if (fresh) setSelected(fresh); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const selectUser = (u) => { window.history.pushState({ cmModal: "user-detail" }, ""); setSubShow(false); setSubRevenue(true); setSelected(u); };
  const backToList = () => window.history.back();
  const doBan = async (u) => { setBusy(true); try { await ban(u.id); } finally { setBusy(false); } };
  const doUnban = async (u) => { setBusy(true); try { await unban(u.id); } finally { setBusy(false); } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="users-modal">
        {!selected ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2"><Users className="h-5 w-5 text-blue-600" />{t("apanel.usersModal.title")}</DialogTitle>
              <DialogDescription>{t("apanel.usersModal.desc")}</DialogDescription>
            </DialogHeader>
            <div className="flex items-baseline justify-between rounded-2xl bg-blue-50 border border-blue-100 px-4 py-3">
              <span className="text-sm text-slate-500">{t("apanel.usersModal.total")}</span>
              <span data-testid="users-modal-total" className="font-display font-extrabold text-2xl text-blue-700">{users.length}</span>
            </div>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input data-testid="users-modal-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("apanel.usersModal.searchPh")} className="pl-9" />
            </div>
            <div className="mt-2 space-y-2">
              {list.map((u) => (
                <button key={u.id} data-testid={`user-row-${u.id}`} onClick={() => selectUser(u)}
                  className="w-full text-left flex items-center gap-3 border border-slate-200 rounded-2xl p-3 transition-colors hover:border-[#008080]">
                  <span className="h-10 w-10 rounded-full bg-slate-100 overflow-hidden shrink-0 grid place-items-center">
                    {u.profile_photo ? <img src={fileUrl(u.profile_photo)} alt="" className="h-full w-full object-cover" /> : <Users className="h-4 w-4 text-slate-400" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm flex items-center gap-1 truncate">
                      {u.name}{u.is_professional && <BadgeCheck className="h-3.5 w-3.5 text-[#008080]" />}{u.role === "admin" && <Shield className="h-3.5 w-3.5 text-slate-400" />}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{u.email}</p>
                  </div>
                  {u.banned && <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 shrink-0">{t("admin.suspended")}</span>}
                  <ExternalLink className="h-4 w-4 text-slate-300 shrink-0" />
                </button>
              ))}
              {list.length === 0 && <p className="text-center py-8 text-slate-400 text-sm">{t("apanel.usersModal.noResults")}</p>}
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <button data-testid="user-detail-back" onClick={backToList} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#008080] mb-1 w-fit">
                <X className="h-4 w-4 rotate-45 hidden" /><span aria-hidden>←</span> {t("apanel.usersModal.back")}
              </button>
              <DialogTitle className="font-display flex items-center gap-2">{t("apanel.usersModal.profileTitle")}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center text-center py-2">
              <span className="h-20 w-20 rounded-full bg-slate-100 overflow-hidden grid place-items-center mb-3">
                {selected.profile_photo ? <img src={fileUrl(selected.profile_photo)} alt="" className="h-full w-full object-cover" /> : <Users className="h-8 w-8 text-slate-400" />}
              </span>
              <p className="font-display font-bold text-xl flex items-center gap-1">{selected.name}{selected.is_professional && <BadgeCheck className="h-4 w-4 text-[#008080]" />}</p>
              <p className="text-sm text-slate-400">@{selected.nickname}</p>
              {selected.banned && <span className="mt-2 text-xs px-3 py-1 rounded-full bg-rose-100 text-rose-700">{t("admin.suspended")}</span>}
            </div>
            <div className="space-y-2 text-sm">
              <Row label={t("apanel.usersModal.email")} value={selected.email} testid="detail-email" />
              <Row label={t("apanel.usersModal.joinDate")} value={formatDate(selected.created_at, lang)} testid="detail-joined" />
              <Row label={t("apanel.usersModal.accountType")} value={accountType(selected, t)} testid="detail-type" />
              <Row label={t("apanel.usersModal.wallet")} value={formatPrice(selected.wallet_balance ?? 0, lang)} testid="detail-wallet" />
              <Row label={t("apanel.usersModal.location")} value={selected.municipality || selected.island || "—"} />
            </div>
            {selected.role !== "admin" && (
              <div className="mt-4 flex gap-2">
                <Button data-testid="detail-view-profile" variant="outline" className="flex-1"
                  onClick={() => { onOpenChange(false); navigate(`/user/${selected.id}`); }}>
                  <Eye className="h-4 w-4 mr-1" />{t("apanel.usersModal.openPublic")}
                </Button>
                {selected.banned
                  ? <Button data-testid="detail-unban-btn" disabled={busy} onClick={() => doUnban(selected)} className="flex-1 bg-emerald-500 hover:bg-emerald-600"><Check className="h-4 w-4 mr-1" />{t("apanel.usersModal.unlockAccount")}</Button>
                  : <Button data-testid="detail-ban-btn" disabled={busy} onClick={() => doBan(selected)} variant="destructive" className="flex-1"><Ban className="h-4 w-4 mr-1" />{t("apanel.usersModal.blockAccount")}</Button>}
              </div>
            )}
            {selected.role !== "admin" && (
              <div className="mt-3">
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 mb-2" data-testid="detail-plan-info">
                  <div className="flex items-center justify-between"><span>{PLAN_L.current}</span>
                    <span className="font-semibold text-slate-800">{(selected.plan || "free").toUpperCase()}{selected.payment_exempt ? " · ∞" : ""}</span></div>
                  {selected.plan_ends && <div className="flex items-center justify-between mt-1"><span>{PLAN_L.until}</span><span className="font-medium">{formatDate(selected.plan_ends, lang)}</span></div>}
                </div>
                {!subShow ? (
                  <Button data-testid="manual-sub-open" onClick={() => setSubShow(true)} variant="outline" className="w-full border-[#008080] text-[#008080] hover:bg-teal-50">
                    <CreditCard className="h-4 w-4 mr-1" />{PLAN_L.activate}
                  </Button>
                ) : (
                  <div className="rounded-xl border border-slate-200 p-3 space-y-3" data-testid="manual-sub-form">
                    <div>
                      <label className="text-xs text-slate-500">{PLAN_L.plan}</label>
                      <select data-testid="manual-sub-plan" value={subPlan} onChange={(e) => setSubPlan(e.target.value)}
                        className="mt-1 w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-teal-500/40">
                        <option value="free">Free</option>
                        <option value="start">Start</option>
                        <option value="pro">Pro</option>
                        <option value="unlimited">Unlimited</option>
                      </select>
                    </div>
                    {subPlan !== "free" && (
                      <div>
                        <label className="text-xs text-slate-500">{PLAN_L.duration}</label>
                        <div className="mt-1 flex gap-2">
                          <div className="flex rounded-lg border border-slate-200 overflow-hidden shrink-0">
                            <button type="button" data-testid="manual-sub-months" onClick={() => setSubType("months")} className={`px-3 text-sm ${subType === "months" ? "bg-[#008080] text-white" : "bg-white text-slate-600"}`}>{PLAN_L.months}</button>
                            <button type="button" data-testid="manual-sub-days" onClick={() => setSubType("days")} className={`px-3 text-sm ${subType === "days" ? "bg-[#008080] text-white" : "bg-white text-slate-600"}`}>{PLAN_L.days}</button>
                          </div>
                          <Input data-testid="manual-sub-value" type="number" min="1" value={subVal} onChange={(e) => setSubVal(e.target.value)} className="flex-1 h-10" />
                        </div>
                      </div>
                    )}
                    {subPlan !== "free" && (
                      <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5" data-testid="manual-sub-revenue">
                        <p className="text-xs font-semibold text-amber-900">{PLAN_L.revenueQ}</p>
                        <p className="text-[11px] text-amber-700 mt-0.5 leading-snug">{PLAN_L.revenueNote}</p>
                        <div className="mt-2 flex gap-2">
                          <button type="button" data-testid="manual-sub-revenue-include" onClick={() => setSubRevenue(true)}
                            className={`flex-1 h-8 rounded-lg text-xs font-semibold border ${subRevenue ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200"}`}>{PLAN_L.include}</button>
                          <button type="button" data-testid="manual-sub-revenue-exclude" onClick={() => setSubRevenue(false)}
                            className={`flex-1 h-8 rounded-lg text-xs font-semibold border ${!subRevenue ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-600 border-slate-200"}`}>{PLAN_L.exclude}</button>
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button data-testid="manual-sub-cancel" variant="outline" className="flex-1" onClick={() => setSubShow(false)}>{PLAN_L.cancel}</Button>
                      <Button data-testid="manual-sub-confirm" disabled={subBusy} className="flex-1 bg-[#008080] hover:bg-[#006666]"
                        onClick={async () => { setSubBusy(true); try { await setSubscription(selected.id, { plan: subPlan, duration_type: subType, duration_value: Number(subVal) || 1, count_in_revenue: subPlan !== "free" && subRevenue }); setSubShow(false); } finally { setSubBusy(false); } }}>
                        {subBusy ? "…" : PLAN_L.confirm}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 py-2">
      <span className="text-slate-400">{label}</span>
      <span data-testid={testid} className="font-medium text-slate-700 truncate max-w-[60%] text-right">{value}</span>
    </div>
  );
}




const PLAN_PRICE = { free: 0, start: 9.90, pro: 19.90, unlimited: 99.90 };

function PlanUsersModal({ open, onOpenChange, list, title, desc, total, count, iconColor, icon: Icon, t, lang }) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) return;
    window.history.pushState({ cmModal: "plan" }, "");
    const onPop = () => onOpenChange(false);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [open, onOpenChange]);
  const openProfile = (id) => { onOpenChange(false); navigate(`/user/${id}`); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="plan-users-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Icon className={`h-5 w-5 ${iconColor}`} />{title}</DialogTitle>
          <DialogDescription>{desc}</DialogDescription>
        </DialogHeader>
        <div className="flex items-baseline justify-between rounded-2xl bg-slate-50 border border-slate-200 px-4 py-3">
          <span className="text-sm text-slate-500">{t("apanel.usersModal.total")}: <b className="text-slate-700">{count}</b></span>
          <span data-testid="plan-modal-total" className="font-display font-extrabold text-2xl text-slate-800">{total != null ? formatPrice(total, lang) : count}</span>
        </div>
        {list.length === 0 ? (
          <p className="text-center py-10 text-slate-400" data-testid="plan-empty">{t("apanel.usersModal.noResults")}</p>
        ) : (
          <div className="mt-3 space-y-2">
            {list.map((u) => (
              <button key={u.id} data-testid={`plan-row-${u.id}`} onClick={() => openProfile(u.id)}
                className="w-full text-left flex items-center gap-3 border border-slate-200 rounded-2xl p-3 hover:border-[#008080] transition-colors">
                <span className="h-10 w-10 rounded-full bg-slate-100 overflow-hidden shrink-0 grid place-items-center">
                  {u.profile_photo ? <img src={fileUrl(u.profile_photo)} alt="" className="h-full w-full object-cover" /> : <Users className="h-4 w-4 text-slate-400" />}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate flex items-center gap-1">{u.name}{u.is_professional && <BadgeCheck className="h-3.5 w-3.5 text-[#008080]" />}</p>
                  <p className="text-xs text-slate-400 truncate">@{u.nickname} · {u.email}</p>
                </div>
                <ExternalLink className="h-4 w-4 text-slate-300 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [flags, setFlags] = useState([]);
  const [config, setConfig] = useState(null);
  const [business, setBusiness] = useState([]);
  const [bizStats, setBizStats] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [boosts, setBoosts] = useState(null);
  const [pendingPayouts, setPendingPayouts] = useState(null);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveKind, setResolveKind] = useState(null);
  const [resolveNote, setResolveNote] = useState("");
  const [flagsUser, setFlagsUser] = useState(null);
  const [userFlags, setUserFlags] = useState([]);
  // user filters
  const [q, setQ] = useState("");
  const [islandFilter, setIslandFilter] = useState("");
  const [sortSold, setSortSold] = useState(false);
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [chartMode, setChartMode] = useState("daily");
  const [range, setRange] = useState({});

  const load = useCallback(async () => {
    const p = (range.from && range.to)
      ? { params: { date_from: fmtDay(range.from), date_to: fmtDay(range.to) } }
      : {};
    try {
      const [s, an, r, u, l, tk, mf, cf, bo] = await Promise.all([
        api.get("/admin/stats", p), api.get("/admin/analytics", p), api.get("/admin/reports"),
        api.get("/admin/users"), api.get("/admin/listings"), api.get("/admin/tickets"),
        api.get("/admin/moderation-flags", p), api.get("/admin/config"), api.get("/admin/boosts", p),
      ]);
      setStats(s.data); setAnalytics(an.data); setReports(r.data); setUsers(u.data);
      setListings(l.data); setTickets(tk.data); setFlags(mf.data); setConfig(cf.data); setBoosts(bo.data);
      setOrders([]); setDisputes([]); setBusiness([]); setBizStats(null); setWithdrawals([]); setPendingPayouts([]);
    } catch { toast.error("No autorizado"); navigate("/"); }
  }, [navigate, range]);

  useEffect(() => {
    if (user === false) navigate("/auth");
    else if (user && user.role !== "admin") navigate("/");
    else if (user) load();
  }, [user, navigate, load]);
  // Real-time: refresh admin data so new reports/tickets/withdrawals appear without reload.
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [user, load]);

  const [delReport, setDelReport] = useState(null);
  const [delReason, setDelReason] = useState("");
  const [gmvOpen, setGmvOpen] = useState(false);
  const [subsOpen, setSubsOpen] = useState(false);
  const [boostsOpen, setBoostsOpen] = useState(false);
  const [payoutsOpen, setPayoutsOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [paidSubsOpen, setPaidSubsOpen] = useState(false);
  const [freeOpen, setFreeOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [proOpen, setProOpen] = useState(false);
  const [unlimitedOpen, setUnlimitedOpen] = useState(false);
  const resolveReport = async (id, action, reason = "") => {
    try { await api.post(`/admin/reports/${id}/resolve`, { action, reason }); toast.success(t("admin.resolved")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const ban = async (id) => { await api.post(`/admin/users/${id}/ban`); toast.success(t("admin.ban")); load(); };
  const unban = async (id) => { await api.post(`/admin/users/${id}/unban`); toast.success(t("admin.unbanned")); load(); };
  const setSubscription = async (id, payload) => {
    await api.post(`/admin/users/${id}/subscription`, payload);
    toast.success(lang === "it" ? "Abbonamento attivato" : lang === "en" ? "Subscription activated" : "Suscripción activada");
    load();
  };
  const unlockPayout = async (o) => {
    try { await api.post(`/admin/orders/${o.id}/unlock`); toast.success(t("apanel.payoutUnlocked")); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const delListing = async (id) => { await api.delete(`/listings/${id}`); toast.success(t("admin.deleteListing")); load(); };
  const openResolve = (order, kind) => { setResolveTarget(order); setResolveKind(kind); setResolveNote(""); };
  const confirmResolve = async () => {
    try {
      await api.post(`/admin/orders/${resolveTarget.id}/resolve`, { resolution: resolveKind, note: resolveNote });
      toast.success(t("admin.orderResolved")); setResolveTarget(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const viewFlags = async (u) => {
    try { const { data } = await api.get(`/admin/users/${u.id}/flags`); setFlagsUser(u); setUserFlags(data); }
    catch { toast.error("Error"); }
  };

  const verifyBiz = async (id) => { await api.post(`/admin/business/${id}/verify`); toast.success(t("apanel.bizVerified")); load(); };
  const rejectBiz = async (id) => {
    const reason = window.prompt(t("apanel.rejectReasonPrompt")) ?? null;
    if (reason === null) return;
    await api.post(`/admin/business/${id}/reject`, { reason }); toast.success(t("apanel.bizRejected")); load();
  };
  const toggleBadge = async (id, active) => { await api.post(`/admin/business/${id}/badge`, { active }); toast.success(t("apanel.saved")); load(); };

  const completeWd = async (id) => { await api.post(`/admin/withdrawals/${id}/complete`); toast.success(t("apanel.saved")); load(); };
  const rejectWd = async (id) => {
    const reason = window.prompt(t("apanel.rejectReasonPrompt")) ?? null;
    if (reason === null) return;
    await api.post(`/admin/withdrawals/${id}/reject`, { reason }); toast.success(t("apanel.saved")); load();
  };

  const filteredUsers = useMemo(() => {
    let list = users;
    if (onlyFlagged) list = list.filter((u) => u.banned || u.moderation_strikes > 0);
    if (islandFilter) list = list.filter((u) => u.island === islandFilter);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((u) => (u.name || "").toLowerCase().includes(s) || (u.nickname || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s) || (u.municipality || "").toLowerCase().includes(s));
    }
    if (sortSold) list = [...list].sort((a, b) => (b.sold_count || 0) - (a.sold_count || 0));
    return list;
  }, [users, q, islandFilter, sortSold, onlyFlagged]);

  const planLists = useMemo(() => {
    const active = (p) => users.filter((u) => u.role !== "admin" && (u.plan || "free") === p && (p === "free" || (u.plan_status || "active") === "active"));
    return { free: active("free"), start: active("start"), pro: active("pro"), unlimited: active("unlimited") };
  }, [users]);

  if (!stats) return <div className="p-10 text-center text-slate-400">{t("common.loading")}</div>;

  const chartData = analytics ? (chartMode === "daily" ? analytics.daily.map((x) => ({ ...x, label: x.date.slice(5) })) : analytics.monthly.map((x) => ({ ...x, label: x.month }))) : [];

  const planTotals = {
    start: planLists.start.length * PLAN_PRICE.start,
    pro: planLists.pro.length * PLAN_PRICE.pro,
    unlimited: planLists.unlimited.length * PLAN_PRICE.unlimited,
  };
  const paidSubs = [...planLists.start, ...planLists.pro, ...planLists.unlimited];
  const subTotal = planTotals.start + planTotals.pro + planTotals.unlimited;
  const platformTotal = subTotal + (stats.boost_revenue || 0);
  const L = {
    users: lang === "it" ? "Utenti iscritti alla piattaforma" : lang === "en" ? "Registered users" : "Usuarios registrados",
    subRev: lang === "it" ? "Ricavi abbonamenti" : lang === "en" ? "Subscription revenue" : "Ingresos suscripciones",
    boostRev: lang === "it" ? "Ricavi boost" : lang === "en" ? "Boost revenue" : "Ingresos boost",
    platform: lang === "it" ? "Ricavi piattaforma" : lang === "en" ? "Platform revenue" : "Ingresos plataforma",
    free: lang === "it" ? "Pacchetti Free attivi" : lang === "en" ? "Active Free plans" : "Paquetes Free activos",
    start: lang === "it" ? "Pacchetti Start attivi" : lang === "en" ? "Active Start plans" : "Paquetes Start activos",
    pro: lang === "it" ? "Pacchetti Pro attivi" : lang === "en" ? "Active Pro plans" : "Paquetes Pro activos",
    unlimited: lang === "it" ? "Pacchetti Unlimited attivi" : lang === "en" ? "Active Unlimited plans" : "Paquetes Unlimited activos",
    tickets: lang === "it" ? "Ticket aperti" : lang === "en" ? "Open tickets" : "Tickets abiertos",
  };

  return (
    <div className="px-4 sm:px-8 py-6">
      <h1 className="font-display font-extrabold text-2xl sm:text-3xl mb-5 flex items-center gap-2"><Shield className="h-6 w-6 text-[#008080]" />{t("admin.title")}</h1>

      {/* Date range filter — updates all metrics/charts/transactions in real time */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <DateRangePicker range={range} onChange={setRange} />
        {range?.from && range?.to && (
          <span data-testid="date-range-active" className="text-xs text-slate-400">{t("apanel.dateFiltered")}</span>
        )}
      </div>

      {/* Section nav */}
      <div className="flex flex-wrap gap-2 mb-6">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const badge = s.id === "withdrawals" ? withdrawals.filter((w) => w.status === "pending").length
            : s.id === "tickets" ? tickets.filter((x) => x.status === "open").length
            : s.id === "moderation" ? reports.filter((x) => x.status === "open").length : 0;
          return (
            <button key={s.id} data-testid={`admin-section-${s.id}`} onClick={() => setSection(s.id)}
              className={`relative flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${section === s.id ? "bg-[#008080] text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              <Icon className="h-4 w-4" />{t(`apanel.${s.id}`)}
              {badge > 0 && (
                <span data-testid={`admin-badge-${s.id}`}
                  className="absolute -top-1.5 -right-1.5 h-5 min-w-5 px-1 grid place-items-center text-[10px] font-bold text-white bg-rose-500 rounded-full shadow-sm ring-2 ring-[#F8FAF9]">
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ---------------- DASHBOARD ---------------- */}
      {section === "dashboard" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <Kpi testid="kpi-users" icon={Users} label={L.users} value={stats.users} color="bg-blue-100 text-blue-700" onClick={() => setUsersOpen(true)} />
            <Kpi testid="kpi-sub-revenue" icon={Repeat} label={L.subRev} value={formatPrice(subTotal, lang)} color="bg-emerald-100 text-emerald-700" onClick={() => setPaidSubsOpen(true)} />
            <Kpi testid="kpi-boost-revenue" icon={Rocket} label={L.boostRev} value={formatPrice(stats.boost_revenue || 0, lang)} color="bg-amber-100 text-amber-700" onClick={() => setBoostsOpen(true)} />
            <Kpi testid="kpi-platform-revenue" icon={TrendingUp} label={L.platform} value={formatPrice(platformTotal, lang)} color="bg-teal-100 text-teal-700" />
            <Kpi testid="kpi-free-users" icon={Users} label={`${L.free} · ${planLists.free.length}`} value={planLists.free.length} color="bg-slate-100 text-slate-700" onClick={() => setFreeOpen(true)} />
            <Kpi testid="kpi-rev-start" icon={Euro} label={`${L.start} · ${planLists.start.length}`} value={formatPrice(planTotals.start, lang)} color="bg-sky-100 text-sky-700" onClick={() => setStartOpen(true)} />
            <Kpi testid="kpi-rev-pro" icon={Euro} label={`${L.pro} · ${planLists.pro.length}`} value={formatPrice(planTotals.pro, lang)} color="bg-indigo-100 text-indigo-700" onClick={() => setProOpen(true)} />
            <Kpi testid="kpi-rev-unlimited" icon={Euro} label={`${L.unlimited} · ${planLists.unlimited.length}`} value={formatPrice(planTotals.unlimited, lang)} color="bg-purple-100 text-purple-700" onClick={() => setUnlimitedOpen(true)} />
            <Kpi testid="kpi-tickets" icon={LifeBuoy} label={L.tickets} value={stats.open_tickets} color="bg-cyan-100 text-cyan-700" onClick={() => setSection("tickets")} />
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg flex items-center gap-2"><TrendingUp className="h-5 w-5 text-[#008080]" />{t("apanel.growthTitle")}</h2>
              <div className="flex gap-1 bg-slate-100 rounded-full p-1">
                {["daily", "monthly"].map((m) => (
                  <button key={m} data-testid={`chart-mode-${m}`} onClick={() => setChartMode(m)}
                    className={`text-xs px-3 py-1 rounded-full ${chartMode === m ? "bg-white shadow text-[#008080] font-semibold" : "text-slate-500"}`}>{t(`apanel.${m}`)}</button>
                ))}
              </div>
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-slate-500 mb-2">{t("apanel.regTrend")} · {t("apanel.listTrend")}</p>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="users" stroke="#008080" strokeWidth={2} dot={false} name={t("apanel.registeredUsers")} />
                    <Line type="monotone" dataKey="listings" stroke="#f59e0b" strokeWidth={2} dot={false} name={t("admin.listings")} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-2">{t("apanel.revTrend")}</p>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={chartData}>
                    <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#008080" stopOpacity={0.4} /><stop offset="100%" stopColor="#008080" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => `${v} €`} />
                    <Area type="monotone" dataKey="revenue" stroke="#008080" strokeWidth={2} fill="url(#rev)" name={t("apanel.revenue")} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- USERS ---------------- */}
      {section === "users" && (
        <Tabs defaultValue="users">
          <TabsList className="mb-4">
            <TabsTrigger value="users" data-testid="admin-tab-users">{t("admin.users")} ({users.length})</TabsTrigger>
            <TabsTrigger value="autobans" data-testid="admin-tab-autobans">{t("apanel.autoBans")} ({users.filter((u) => u.banned || u.moderation_strikes > 0).length})</TabsTrigger>
            <TabsTrigger value="listings" data-testid="admin-tab-listings">{t("admin.listings")}</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input data-testid="user-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("apanel.search")} className="pl-9" />
              </div>
              <select data-testid="user-island-filter" value={islandFilter} onChange={(e) => setIslandFilter(e.target.value)} className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm">
                <option value="">{t("apanel.allIslands")}</option>
                {Object.keys(ISLAND_NAME).map((i) => <option key={i} value={i}>{ISLAND_NAME[i]}</option>)}
              </select>
              <button data-testid="user-sort-sold" onClick={() => setSortSold((v) => !v)} className={`h-10 px-3 rounded-lg border text-sm ${sortSold ? "bg-[#008080] text-white border-[#008080]" : "border-slate-200 text-slate-600"}`}>{t("apanel.sortSold")}</button>
            </div>
            <UsersList list={filteredUsers} t={t} ban={ban} unban={unban} viewFlags={viewFlags} />
          </TabsContent>

          <TabsContent value="autobans">
            <UsersList list={users.filter((u) => u.banned || u.moderation_strikes > 0)} t={t} ban={ban} unban={unban} viewFlags={viewFlags} emptyText={t("apanel.noFlagged")} />
          </TabsContent>

          <TabsContent value="listings">
            <div className="space-y-2">
              {listings.map((l) => (
                <div key={l.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3">
                  <Link to={`/listing/${l.id}`} className="h-12 w-12 rounded-lg overflow-hidden bg-slate-100 shrink-0">{l.photos?.[0] && <img src={fileUrl(l.photos[0])} alt="" className="w-full h-full object-cover" />}</Link>
                  <div className="flex-1 min-w-0"><p className="font-medium text-sm truncate">{l.title}</p><p className="text-xs text-slate-400"><span className="font-mono text-slate-500">{l.ref || "—"}</span> · {l.seller?.name} · {l.status}</p></div>
                  <Button data-testid={`admin-del-${l.id}`} onClick={() => delListing(l.id)} variant="outline" className="h-8 text-xs text-rose-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      )}

      {/* ---------------- ORDERS ---------------- */}
      {section === "orders" && (
        <Tabs defaultValue="disputes">
          <TabsList className="mb-4">
            <TabsTrigger value="disputes" data-testid="admin-tab-disputes">{t("admin.disputesQueue")} ({disputes.length})</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders">{t("admin.ordersQueue")} ({orders.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="disputes">
            {disputes.length === 0 ? <div className="text-center py-16 text-slate-400">{t("admin.noDisputes")}</div> : (
              <div className="space-y-3">{disputes.map((o) => <AdminOrderCard key={o.id} order={o} t={t} lang={lang} onResolve={openResolve} showResolve />)}</div>
            )}
          </TabsContent>
          <TabsContent value="orders">
            {orders.length === 0 ? <div className="text-center py-16 text-slate-400">{t("admin.noOrders")}</div> : (
              <div className="space-y-3">{orders.map((o) => <AdminOrderCard key={o.id} order={o} t={t} lang={lang} onResolve={openResolve} showResolve={o.disputed && o.dispute?.status === "open"} />)}</div>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* ---------------- MODERATION (reports + blocked messages) ---------------- */}
      {section === "moderation" && (
        <div data-testid="moderation-panel">
          <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Flag className="h-5 w-5 text-rose-600" />{t("admin.reportsQueue")} ({reports.filter((x) => x.status === "open").length})</h2>
          {reports.length === 0 ? <div className="text-center py-10 text-slate-400">{t("admin.noReports")}</div> : (
            <div className="space-y-3 mb-8">
              {reports.map((r) => (
                <div key={r.id} data-testid={`report-${r.id}`} className={`bg-white border rounded-2xl p-4 ${r.status === "open" ? "border-rose-200" : "border-slate-200 opacity-60"}`}>
                  <div className="flex items-start gap-3">
                    {r.listing?.photos?.[0] && <Link to={`/listing/${r.listing.id}`} className="h-14 w-14 rounded-xl overflow-hidden bg-slate-100 shrink-0"><img src={fileUrl(r.listing.photos[0])} alt="" className="w-full h-full object-cover" /></Link>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {r.listing_ref && <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{r.listing_ref}</span>}
                        <p className="font-medium text-sm truncate">{r.listing_title || t("admin.listingDeleted")}</p>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{t("admin.reporterLabel")}: <b>{r.reporter_name || "—"}</b> · {t("listing.seller")}: <b>{r.seller_name || "—"}</b></p>
                      <p className="text-xs text-rose-600 mt-1">{t(`report.reasons.${r.reason}`)}</p>
                      {r.comment && <p className="text-xs text-slate-500 mt-0.5 break-words">"{r.comment}"</p>}
                      {r.listing?.id && <Link data-testid={`report-open-${r.id}`} to={`/listing/${r.listing.id}`} className="inline-flex items-center gap-1 text-xs text-[#008080] hover:underline mt-1"><ExternalLink className="h-3 w-3" />{t("admin.viewListing")}</Link>}
                    </div>
                    <span className="text-[10px] px-2 py-1 rounded-full bg-slate-100 shrink-0">{r.status === "open" ? t("admin.reportOpen") : t("admin.reportResolved")}</span>
                  </div>
                  {r.status === "open" && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Button data-testid={`report-delete-${r.id}`} onClick={() => { setDelReport(r); setDelReason(""); }} variant="destructive" className="h-8 text-xs"><Trash2 className="h-3.5 w-3.5 mr-1" />{t("admin.deleteListing")}</Button>
                      <Button data-testid={`report-compliant-${r.id}`} onClick={() => resolveReport(r.id, "compliant")} variant="outline" className="h-8 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50"><Check className="h-3.5 w-3.5 mr-1" />{t("admin.markCompliant")}</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><MessageSquareWarning className="h-5 w-5 text-orange-600" />{t("apanel.blockedLog")} ({flags.length})</h2>
          {flags.length === 0 ? <div className="text-center py-10 text-slate-400">{t("apanel.noBlocked")}</div> : (
            <div className="space-y-2">
              {flags.map((f) => (
                <div key={f.id} data-testid={`flag-${f.id}`} className="bg-white border border-slate-200 rounded-xl p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="h-8 w-8 grid place-items-center rounded-lg bg-orange-100 text-orange-700 shrink-0"><MessageSquareWarning className="h-4 w-4" /></span>
                    <button onClick={() => f.user && navigate(`/user/${f.user.id}`)} className="font-medium text-sm hover:underline">{f.user?.name || "—"}</button>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">{t(`chatSafety.reasons.${f.reason}`)}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{t("apanel.attempt")} {f.strike || "-"}</span>
                    <span className="text-[11px] text-slate-400 ml-auto">{timeAgo(f.created_at, lang)}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-2 bg-slate-50 rounded-lg px-3 py-2 break-words">"{f.text}"</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------------- TICKETS ---------------- */}
      {section === "tickets" && (
        <div data-testid="tickets-panel">
          <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><LifeBuoy className="h-5 w-5 text-cyan-600" />{t("admin.ticketsQueue")} ({tickets.filter((x) => x.status === "open").length})</h2>
          {tickets.length === 0 ? <div className="text-center py-16 text-slate-400">{t("admin.noTickets")}</div> : (
            <div className="space-y-3">
              {tickets.map((tk) => (
                <button key={tk.id} data-testid={`admin-ticket-${tk.id}`} onClick={() => navigate(`/support?ticket=${tk.id}`)}
                  className={`w-full text-left bg-white border rounded-2xl p-4 hover:border-[#008080] transition-colors ${tk.status === "open" ? "border-cyan-200" : "border-slate-200 opacity-70"}`}>
                  <div className="flex items-center gap-3">
                    <span className="h-10 w-10 grid place-items-center rounded-xl bg-cyan-100 text-cyan-700 shrink-0"><LifeBuoy className="h-4.5 w-4.5" /></span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{tk.subject}</p>
                      <p className="text-xs text-slate-400 truncate">{tk.user?.name} · {t(`support.categories.${tk.category}`)} · {tk.last_message}</p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${tk.status === "open" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{t(`support.status.${tk.status}`)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------------- WITHDRAWALS ---------------- */}
      {section === "withdrawals" && (
        <div className="space-y-3" data-testid="withdrawals-panel">
          {withdrawals.length === 0 ? (
            <div className="text-center py-12 text-slate-400">{t("apanel.noWithdrawals")}</div>
          ) : withdrawals.map((w) => (
            <div key={w.id} data-testid={`wd-admin-${w.id}`} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="font-display font-bold text-base flex items-center gap-2">
                  {formatPrice(w.amount, lang)}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${w.status === "pending" ? "bg-amber-100 text-amber-700" : w.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{t(`wallet.wd_${w.status}`)}</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">{w.holder} · <span className="font-mono">{w.iban}</span></p>
                <p className="text-xs text-slate-400 mt-0.5">{w.user?.name || "—"} · {formatDate(w.created_at, lang)}</p>
                {w.reason && <p className="text-xs text-rose-500 mt-0.5">{w.reason}</p>}
              </div>
              {w.status === "pending" && (
                <div className="flex gap-2">
                  <Button data-testid={`wd-complete-${w.id}`} onClick={() => completeWd(w.id)} className="h-9 bg-emerald-500 hover:bg-emerald-600"><Check className="h-4 w-4 mr-1" />{t("apanel.markPaid")}</Button>
                  <Button data-testid={`wd-reject-${w.id}`} onClick={() => rejectWd(w.id)} variant="outline" className="h-9 text-rose-600 border-rose-200 hover:bg-rose-50"><X className="h-4 w-4 mr-1" />{t("apanel.rejectBtn")}</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ---------------- BUSINESS ---------------- */}
      {section === "business" && bizStats && (
        <BusinessPanel biz={business} stats={bizStats} t={t} lang={lang}
          onVerify={verifyBiz} onReject={rejectBiz} onToggleBadge={toggleBadge} />
      )}

      {/* ---------------- CONFIG ---------------- */}
      {section === "config" && config && (
        <ConfigPanel config={config} t={t} lang={lang} stats={stats} onSaved={load}
          subTotal={subTotal} boostRevenue={stats.boost_revenue || 0} platformTotal={platformTotal}
          onOpenSubs={() => setPaidSubsOpen(true)} onOpenBoosts={() => setBoostsOpen(true)} />
      )}

      {/* Resolve dispute dialog */}
      <Dialog open={!!resolveTarget} onOpenChange={(o) => !o && setResolveTarget(null)}>
        <DialogContent className="rounded-2xl" data-testid="resolve-dialog">
          <DialogHeader><DialogTitle className="font-display">{resolveKind === "release" ? t("admin.releaseFunds") : t("admin.refundBuyer")}</DialogTitle></DialogHeader>
          {resolveTarget && (
            <div className="space-y-4">
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm">
                <p className="font-medium truncate">{resolveTarget.listing_title}</p>
                <p className="text-xs text-slate-400">{resolveTarget.buyer?.name} → {resolveTarget.seller?.name} · {formatPrice(resolveTarget.total ?? resolveTarget.amount, lang)}</p>
                {resolveTarget.carrier && <p className="text-xs text-slate-500 mt-1">{courierLabel(resolveTarget)} · {resolveTarget.tracking_code}</p>}
              </div>
              <Textarea data-testid="resolve-note-input" value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} placeholder={t("admin.resolveNote")} rows={2} />
              <Button data-testid="resolve-confirm-btn" onClick={confirmResolve} className={`w-full h-11 ${resolveKind === "release" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-rose-500 hover:bg-rose-600"}`}>
                {resolveKind === "release" ? t("admin.releaseFunds") : t("admin.refundBuyer")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* User flags history dialog */}
      <Dialog open={!!flagsUser} onOpenChange={(o) => { if (!o) { setFlagsUser(null); setUserFlags([]); } }}>
        <DialogContent className="rounded-2xl max-h-[80vh] overflow-y-auto" data-testid="user-flags-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><MessageSquareWarning className="h-5 w-5 text-orange-500" />{t("apanel.attemptsHistory")}</DialogTitle></DialogHeader>
          {flagsUser && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{flagsUser.name} · {flagsUser.moderation_strikes || 0}/3 · {flagsUser.banned ? t("admin.autoSuspended") : t("apanel.active")}</p>
              {userFlags.length === 0 ? <p className="text-sm text-slate-400">{t("apanel.noBlocked")}</p> : userFlags.map((f) => (
                <div key={f.id} className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-sm">
                  <div className="flex items-center gap-2"><span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">{t(`chatSafety.reasons.${f.reason}`)}</span><span className="text-[11px] text-slate-400 ml-auto">{timeAgo(f.created_at, lang)}</span></div>
                  <p className="text-slate-600 mt-1 break-words">"{f.text}"</p>
                </div>
              ))}
              <div className="flex gap-2 pt-2">
                {flagsUser.banned
                  ? <Button data-testid="flags-unban-btn" onClick={() => { unban(flagsUser.id); setFlagsUser(null); }} className="flex-1 bg-emerald-500 hover:bg-emerald-600"><Check className="h-4 w-4 mr-1" />{t("admin.unban")}</Button>
                  : <Button data-testid="flags-ban-btn" onClick={() => { ban(flagsUser.id); setFlagsUser(null); }} variant="destructive" className="flex-1"><Ban className="h-4 w-4 mr-1" />{t("apanel.confirmBan")}</Button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete reported listing — reason dialog */}
      <Dialog open={!!delReport} onOpenChange={(o) => { if (!o) setDelReport(null); }}>
        <DialogContent className="rounded-2xl" data-testid="delete-report-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Trash2 className="h-5 w-5 text-rose-600" />{t("admin.deleteReportTitle")}</DialogTitle></DialogHeader>
          {delReport && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{delReport.listing_ref && <span className="font-mono text-slate-500">{delReport.listing_ref} · </span>}{delReport.listing_title}</p>
              <div>
                <label className="text-sm font-medium">{t("admin.deleteReportReason")}</label>
                <Textarea data-testid="delete-report-reason" value={delReason} onChange={(e) => setDelReason(e.target.value)} rows={3} placeholder={t("admin.deleteReportReasonPh")} className="mt-1" />
                <p className="text-[11px] text-slate-400 mt-1">{t("admin.deleteReportNote")}</p>
              </div>
              <Button data-testid="delete-report-confirm" onClick={() => { resolveReport(delReport.id, "delete", delReason); setDelReport(null); }} variant="destructive" className="w-full">
                <Trash2 className="h-4 w-4 mr-1" />{t("admin.deleteListing")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CompletedOrdersModal open={gmvOpen} onOpenChange={setGmvOpen} orders={orders} t={t} lang={lang} />
      <ActiveSubsModal open={subsOpen} onOpenChange={setSubsOpen} business={business} t={t} lang={lang} />
      <BoostsModal open={boostsOpen} onOpenChange={setBoostsOpen} boosts={boosts} t={t} lang={lang} />
      <PendingPayoutsModal open={payoutsOpen} onOpenChange={setPayoutsOpen} data={pendingPayouts} t={t} lang={lang} onUnlock={unlockPayout} />
      <UsersModal open={usersOpen} onOpenChange={setUsersOpen} users={users} t={t} lang={lang} ban={ban} unban={unban} setSubscription={setSubscription} />
      <PlanUsersModal open={paidSubsOpen} onOpenChange={setPaidSubsOpen} list={paidSubs} total={subTotal} count={paidSubs.length}
        title={L.subRev} desc={lang === "it" ? "Tutti gli abbonamenti attivi a pagamento" : lang === "en" ? "All active paid subscriptions" : "Todas las suscripciones activas de pago"}
        icon={Repeat} iconColor="text-emerald-600" t={t} lang={lang} />
      <PlanUsersModal open={freeOpen} onOpenChange={setFreeOpen} list={planLists.free} total={null} count={planLists.free.length}
        title={L.free} desc={lang === "it" ? "Utenti con piano Free attivo" : lang === "en" ? "Users on the Free plan" : "Usuarios con plan Free activo"}
        icon={Users} iconColor="text-slate-500" t={t} lang={lang} />
      <PlanUsersModal open={startOpen} onOpenChange={setStartOpen} list={planLists.start} total={planTotals.start} count={planLists.start.length}
        title={L.start} desc={lang === "it" ? "Utenti con piano Start attivo" : lang === "en" ? "Users on the Start plan" : "Usuarios con plan Start activo"}
        icon={Euro} iconColor="text-sky-600" t={t} lang={lang} />
      <PlanUsersModal open={proOpen} onOpenChange={setProOpen} list={planLists.pro} total={planTotals.pro} count={planLists.pro.length}
        title={L.pro} desc={lang === "it" ? "Utenti con piano Pro attivo" : lang === "en" ? "Users on the Pro plan" : "Usuarios con plan Pro activo"}
        icon={Euro} iconColor="text-indigo-600" t={t} lang={lang} />
      <PlanUsersModal open={unlimitedOpen} onOpenChange={setUnlimitedOpen} list={planLists.unlimited} total={planTotals.unlimited} count={planLists.unlimited.length}
        title={L.unlimited} desc={lang === "it" ? "Utenti con piano Unlimited attivo" : lang === "en" ? "Users on the Unlimited plan" : "Usuarios con plan Unlimited activo"}
        icon={Euro} iconColor="text-purple-600" t={t} lang={lang} />
    </div>
  );
}

function UsersList({ list, t, ban, unban, viewFlags, emptyText }) {
  if (list.length === 0) return <div className="text-center py-16 text-slate-400">{emptyText || "—"}</div>;
  return (
    <div className="space-y-2">
      {list.map((u) => (
        <div key={u.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm flex items-center gap-1 flex-wrap">
              {u.name}{u.is_professional && <BadgeCheck className="h-3.5 w-3.5 text-[#008080]" />}{u.role === "admin" && <Shield className="h-3.5 w-3.5 text-slate-400" />}
              {u.banned && <span data-testid={`banned-badge-${u.id}`} className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">{u.ban_reason === "moderation" ? t("admin.autoSuspended") : t("admin.suspended")}</span>}
              {!u.banned && u.moderation_strikes > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{u.moderation_strikes}/3</span>}
            </p>
            <p className="text-xs text-slate-400">@{u.nickname} · {u.municipality || "—"} · {u.sold_count || 0} {t("profile.sold")}</p>
          </div>
          {(u.banned || u.moderation_strikes > 0) && <Button data-testid={`view-flags-${u.id}`} onClick={() => viewFlags(u)} variant="outline" className="h-8 text-xs"><Eye className="h-3.5 w-3.5 mr-1" />{t("apanel.viewAttempts")}</Button>}
          {u.role !== "admin" && (u.banned
            ? <Button data-testid={`unban-user-${u.id}`} onClick={() => unban(u.id)} variant="outline" className="h-8 text-xs text-emerald-600 border-emerald-200"><Check className="h-3.5 w-3.5 mr-1" />{t("admin.unban")}</Button>
            : <Button data-testid={`ban-user-${u.id}`} onClick={() => ban(u.id)} variant="outline" className="h-8 text-xs text-rose-600"><Ban className="h-3.5 w-3.5 mr-1" />{t("admin.ban")}</Button>)}
        </div>
      ))}
    </div>
  );
}

function ConfigPanel({ config, t, lang, stats, onSaved, subTotal, boostRevenue, platformTotal, onOpenSubs, onOpenBoosts }) {
  const [pkgs, setPkgs] = useState(() => Object.entries(config.boost_packages).map(([days, price]) => ({ days, price })).sort((a, b) => Number(a.days) - Number(b.days)));
  const [pct, setPct] = useState(Math.round((config.commission_rate || 0.1) * 1000) / 10);
  const [saving, setSaving] = useState(false);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeText, setWipeText] = useState("");
  const [wiping, setWiping] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const boost_packages = {}; pkgs.forEach((p) => { boost_packages[p.days] = Number(p.price); });
      await api.put("/admin/config", { boost_packages });
      toast.success(t("apanel.saved")); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const runWipe = async () => {
    setWiping(true);
    try {
      const { data } = await api.post("/admin/maintenance/wipe", { confirm: "WIPE-ALL" });
      toast.success(lang === "it" ? `Pulizia completata · ${data.users_deleted} utenti, ${data.listings} annunci eliminati` : `Cleanup done · ${data.users_deleted} users, ${data.listings} listings removed`);
      setWipeOpen(false); setWipeText(""); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setWiping(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 max-w-4xl">
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-1"><Euro className="h-5 w-5 text-[#008080]" />{lang === "it" ? "Ricavi piattaforma" : lang === "en" ? "Platform revenue" : "Ingresos plataforma"}</h2>
        <p className="text-xs text-slate-500 mb-4">{lang === "it" ? "0% di commissioni sulle vendite. I ricavi arrivano solo da abbonamenti e boost." : lang === "en" ? "0% sales commission. Revenue comes only from subscriptions and boosts." : "0% de comisión en ventas. Los ingresos provienen solo de suscripciones y boosts."}</p>
        <div className="space-y-2 text-sm">
          <button data-testid="config-subs-row" onClick={onOpenSubs} className="w-full flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-emerald-800 hover:border-emerald-400 transition-colors">
            <span className="flex items-center gap-1.5"><Repeat className="h-4 w-4" />{lang === "it" ? "Abbonamenti" : lang === "en" ? "Subscriptions" : "Suscripciones"}</span>
            <span className="flex items-center gap-1.5"><b>{formatPrice(subTotal || 0, lang)}</b><ExternalLink className="h-3.5 w-3.5 opacity-60" /></span>
          </button>
          <button data-testid="config-boost-row" onClick={onOpenBoosts} className="w-full flex items-center justify-between rounded-lg bg-amber-50 border border-amber-200 p-3 text-amber-800 hover:border-amber-400 transition-colors">
            <span className="flex items-center gap-1.5"><Rocket className="h-4 w-4" />{t("apanel.boostRev")}</span>
            <span className="flex items-center gap-1.5"><b>{formatPrice(boostRevenue || 0, lang)}</b><ExternalLink className="h-3.5 w-3.5 opacity-60" /></span>
          </button>
          <div className="flex items-center justify-between rounded-lg bg-teal-50 border border-teal-200 p-3 text-teal-800">
            <span>Total</span><b>{formatPrice(platformTotal || 0, lang)}</b>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-4"><Rocket className="h-5 w-5 text-amber-500" />{t("apanel.boostTitle")}</h2>
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2 text-xs text-slate-400 px-1"><span>{t("apanel.days")}</span><span>{t("apanel.price")} (€)</span></div>
          {pkgs.map((p, i) => (
            <div key={p.days} className="grid grid-cols-2 gap-2">
              <Input value={p.days} disabled className="bg-slate-50" />
              <Input data-testid={`boost-price-${p.days}`} type="number" step="0.01" value={p.price} onChange={(e) => setPkgs((prev) => prev.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} />
            </div>
          ))}
        </div>
      </div>

      <div className="lg:col-span-2">
        <Button data-testid="config-save-btn" onClick={save} disabled={saving} className="h-11 bg-[#008080] hover:bg-[#006666] px-8">{saving ? t("common.loading") : t("apanel.save")}</Button>
      </div>

      <div className="lg:col-span-2">
        <EmailTestPanel t={t} />
      </div>

      <CouponsPanel t={t} lang={lang} />

      <div className="lg:col-span-2 bg-white border border-rose-200 rounded-2xl p-5">
        <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-1 text-rose-700"><AlertTriangle className="h-5 w-5" />{lang === "it" ? "Zona pericolosa" : lang === "en" ? "Danger zone" : "Zona peligrosa"}</h2>
        <p className="text-xs text-slate-500 mb-4">{lang === "it" ? "Elimina definitivamente tutti gli utenti (tranne gli admin), annunci, abbonamenti, transazioni, pagamenti, chat e messaggi. Azzera tutti i contatori. Operazione irreversibile." : lang === "en" ? "Permanently delete all users (except admins), listings, subscriptions, transactions, payments, chats and messages. Resets all counters. Irreversible." : "Elimina definitivamente todos los usuarios (excepto admins), anuncios, suscripciones, transacciones, pagos, chats y mensajes. Reinicia todos los contadores. Irreversible."}</p>
        <Button data-testid="admin-wipe-btn" onClick={() => setWipeOpen(true)} className="h-11 bg-rose-600 hover:bg-rose-700 px-6"><Trash2 className="h-4 w-4 mr-2" />{lang === "it" ? "Pulizia completa del database" : lang === "en" ? "Full database cleanup" : "Limpieza total de la base de datos"}</Button>
      </div>

      <Dialog open={wipeOpen} onOpenChange={(o) => { setWipeOpen(o); if (!o) setWipeText(""); }}>
        <DialogContent className="rounded-2xl" data-testid="admin-wipe-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-rose-700"><AlertTriangle className="h-5 w-5" />{lang === "it" ? "Conferma pulizia completa" : lang === "en" ? "Confirm full cleanup" : "Confirmar limpieza total"}</DialogTitle>
            <DialogDescription>{lang === "it" ? "Questa azione è irreversibile. Elimina tutti i dati mantenendo solo gli account admin. Per confermare, scrivi WIPE-ALL qui sotto." : lang === "en" ? "This action is irreversible. Deletes all data keeping only admin accounts. To confirm, type WIPE-ALL below." : "Esta acción es irreversible. Elimina todos los datos manteniendo solo las cuentas admin. Para confirmar, escribe WIPE-ALL abajo."}</DialogDescription>
          </DialogHeader>
          <Input data-testid="admin-wipe-input" value={wipeText} onChange={(e) => setWipeText(e.target.value)} placeholder="WIPE-ALL" className="mt-1" />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => { setWipeOpen(false); setWipeText(""); }} className="h-10">{t("common.cancel") || "Annulla"}</Button>
            <Button data-testid="admin-wipe-confirm-btn" onClick={runWipe} disabled={wipeText !== "WIPE-ALL" || wiping} className="h-10 bg-rose-600 hover:bg-rose-700">{wiping ? t("common.loading") : (lang === "it" ? "Elimina tutto" : lang === "en" ? "Delete everything" : "Eliminar todo")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CouponsPanel({ t, lang }) {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ code: "", discount_type: "percent", value: "", plan: "all", expires_at: "", max_uses: "", active: true });
  const [saving, setSaving] = useState(false);
  const L = {
    it: { title: "Coupon", note: "Crea e gestisci i codici sconto per gli abbonamenti.", code: "Codice", type: "Tipo di sconto", percent: "Percentuale (%)", fixed: "Importo fisso (€)", value: "Valore", plan: "Piano", all: "Tutti", expires: "Scadenza (opzionale)", maxUses: "Max utilizzi (opzionale)", create: "Crea coupon", used: "Utilizzi", status: "Stato", actions: "Azioni", active: "Attivo", inactive: "Inattivo", empty: "Nessun coupon creato", noExpiry: "—", confirmDel: "Eliminare questo coupon?" },
    en: { title: "Coupons", note: "Create and manage discount codes for subscriptions.", code: "Code", type: "Discount type", percent: "Percentage (%)", fixed: "Fixed amount (€)", value: "Value", plan: "Plan", all: "All", expires: "Expiry (optional)", maxUses: "Max uses (optional)", create: "Create coupon", used: "Uses", status: "Status", actions: "Actions", active: "Active", inactive: "Inactive", empty: "No coupons yet", noExpiry: "—", confirmDel: "Delete this coupon?" },
    es: { title: "Cupones", note: "Crea y gestiona códigos de descuento para las suscripciones.", code: "Código", type: "Tipo de descuento", percent: "Porcentaje (%)", fixed: "Importe fijo (€)", value: "Valor", plan: "Plan", all: "Todos", expires: "Caducidad (opcional)", maxUses: "Usos máx. (opcional)", create: "Crear cupón", used: "Usos", status: "Estado", actions: "Acciones", active: "Activo", inactive: "Inactivo", empty: "Aún no hay cupones", noExpiry: "—", confirmDel: "¿Eliminar este cupón?" },
  }[lang] || {};

  const load = () => api.get("/admin/coupons").then(({ data }) => setCoupons(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code.trim() || !form.value) { toast.error(L.code + " / " + L.value); return; }
    setSaving(true);
    try {
      await api.post("/admin/coupons", {
        code: form.code.trim(), discount_type: form.discount_type, value: Number(form.value),
        plan: form.plan, expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        max_uses: form.max_uses ? Number(form.max_uses) : null, active: form.active,
      });
      toast.success("OK"); setForm({ code: "", discount_type: "percent", value: "", plan: "all", expires_at: "", max_uses: "", active: true }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  const toggle = async (c) => { try { await api.patch(`/admin/coupons/${c.id}/toggle`); load(); } catch (e) { toast.error("Error"); } };
  const del = async (c) => { if (!window.confirm(L.confirmDel)) return; try { await api.delete(`/admin/coupons/${c.id}`); load(); } catch (e) { toast.error("Error"); } };

  const inputCls = "h-10 rounded-lg border border-slate-200 px-3 text-sm w-full";
  return (
    <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5" data-testid="coupons-panel">
      <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-1"><Ticket className="h-5 w-5 text-[#008080]" />{L.title}</h2>
      <p className="text-xs text-slate-500 mb-4">{L.note}</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
        <div><label className="text-xs text-slate-500">{L.code}</label><Input data-testid="coupon-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="ESTATE20" className="mt-1" /></div>
        <div><label className="text-xs text-slate-500">{L.type}</label>
          <select data-testid="coupon-type-select" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className={`${inputCls} mt-1`}>
            <option value="percent">{L.percent}</option><option value="fixed">{L.fixed}</option>
          </select></div>
        <div><label className="text-xs text-slate-500">{L.value}</label><Input data-testid="coupon-value-input" type="number" min="0" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1" /></div>
        <div><label className="text-xs text-slate-500">{L.plan}</label>
          <select data-testid="coupon-plan-select" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className={`${inputCls} mt-1`}>
            <option value="all">{L.all}</option><option value="start">Start</option><option value="pro">Pro</option><option value="unlimited">Unlimited</option>
          </select></div>
        <div><label className="text-xs text-slate-500">{L.expires}</label><Input data-testid="coupon-expiry-input" type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1" /></div>
        <div><label className="text-xs text-slate-500">{L.maxUses}</label><Input data-testid="coupon-maxuses-input" type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="mt-1" /></div>
      </div>
      <div className="flex items-center gap-3 mb-6">
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="coupon-active-check" /> {L.active}</label>
        <Button data-testid="coupon-create-btn" onClick={create} disabled={saving} className="h-10 bg-[#008080] hover:bg-[#006666] gap-2 ml-auto"><Plus className="h-4 w-4" />{saving ? t("common.loading") : L.create}</Button>
      </div>

      {coupons.length === 0 ? (
        <p className="text-center text-slate-400 py-6 text-sm" data-testid="coupons-empty">{L.empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="coupons-table">
            <thead><tr className="text-left text-slate-400 border-b border-slate-100">
              <th className="py-2 pr-3">{L.code}</th><th className="py-2 pr-3">{L.type}</th><th className="py-2 pr-3">{L.plan}</th>
              <th className="py-2 pr-3">{L.expires}</th><th className="py-2 pr-3">{L.used}</th><th className="py-2 pr-3">{L.status}</th><th className="py-2 text-right">{L.actions}</th>
            </tr></thead>
            <tbody>
              {coupons.map((c) => (
                <tr key={c.id} data-testid={`coupon-row-${c.code}`} className="border-b border-slate-50">
                  <td className="py-2.5 pr-3 font-semibold">{c.code}</td>
                  <td className="py-2.5 pr-3">{c.discount_type === "percent" ? `${c.value}%` : `${c.value}€`}</td>
                  <td className="py-2.5 pr-3 capitalize">{c.plan === "all" ? L.all : c.plan}</td>
                  <td className="py-2.5 pr-3">{c.expires_at ? formatDate(c.expires_at, lang) : L.noExpiry}</td>
                  <td className="py-2.5 pr-3">{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                  <td className="py-2.5 pr-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.active ? L.active : L.inactive}</span></td>
                  <td className="py-2.5 text-right whitespace-nowrap">
                    <button data-testid={`coupon-toggle-${c.code}`} onClick={() => toggle(c)} title={c.active ? L.inactive : L.active} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Power className="h-4 w-4" /></button>
                    <button data-testid={`coupon-delete-${c.code}`} onClick={() => del(c)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500 ml-1"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EmailTestPanel({ t }) {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [to, setTo] = useState(user?.email || "");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/email-config").then((r) => setCfg(r.data)).catch(() => setCfg(null));
  }, []);

  const send = async () => {
    if (!to) { toast.error(t("apanel.email.needTo")); return; }
    setSending(true);
    try {
      await api.post("/admin/test-email", { to });
      toast.success(t("apanel.email.sent"));
    } catch (e) { toast.error(e.response?.data?.detail || t("apanel.email.error")); }
    finally { setSending(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="email-test-panel">
      <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-1"><Mail className="h-5 w-5 text-[#008080]" />{t("apanel.email.title")}</h2>
      <p className="text-xs text-slate-500 mb-4">{t("apanel.email.note")}</p>

      {cfg && (
        <div className={`flex items-center gap-2 rounded-lg border p-3 text-xs mb-4 ${cfg.configured ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`} data-testid="email-config-status">
          {cfg.configured ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
          <span>
            {cfg.configured
              ? <>{t("apanel.email.configured")} · <b>{cfg.sender_name}</b> &lt;{cfg.sender_address}&gt;</>
              : t("apanel.email.notConfigured")}
          </span>
        </div>
      )}

      <label className="text-sm text-slate-600">{t("apanel.email.toLabel")}</label>
      <div className="flex flex-col sm:flex-row gap-2 mt-1">
        <Input data-testid="test-email-input" type="email" placeholder="destinatario@ejemplo.com" value={to} onChange={(e) => setTo(e.target.value)} className="flex-1" />
        <Button data-testid="test-email-send-btn" onClick={send} disabled={sending || (cfg && !cfg.configured)} className="h-10 bg-[#008080] hover:bg-[#006666] gap-2">
          <Send className="h-4 w-4" />{sending ? t("common.loading") : t("apanel.email.sendBtn")}
        </Button>
      </div>
    </div>
  );
}

const VS_BADGE = {
  verified: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  rejected: "bg-rose-100 text-rose-700",
};

function BusinessPanel({ biz, stats, t, lang, onVerify, onReject, onToggleBadge }) {
  const [filter, setFilter] = useState("all");
  const list = filter === "all" ? biz : biz.filter((b) => (b.verification_status || "pending") === filter);
  const catLabel = (c) => (c ? t(`monetization.business.cats.${c}`) : "—");

  return (
    <div className="space-y-6" data-testid="business-panel">
      {/* MRR metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi testid="biz-kpi-mrr" icon={Repeat} label={t("apanel.mrr")} value={formatPrice(stats.mrr, lang)} color="bg-teal-100 text-teal-700" />
        <Kpi testid="biz-kpi-active" icon={Store} label={t("apanel.activeSubs")} value={stats.active} color="bg-emerald-100 text-emerald-700" />
        <Kpi testid="biz-kpi-canceling" icon={X} label={t("apanel.canceling")} value={stats.canceling} color="bg-amber-100 text-amber-700" />
        <Kpi testid="biz-kpi-pending" icon={AlertTriangle} label={t("apanel.pendingVerif")} value={stats.pending} color="bg-orange-100 text-orange-700" />
        <Kpi testid="biz-kpi-verified" icon={ShieldCheck} label={t("apanel.verifiedCount")} value={stats.verified} color="bg-cyan-100 text-cyan-700" />
        <Kpi testid="biz-kpi-revmonth" icon={Euro} label={t("apanel.subRevMonth")} value={formatPrice(stats.subscription_revenue_month, lang)} color="bg-blue-100 text-blue-700" />
      </div>

      {/* Upcoming renewals / expirations */}
      {stats.upcoming?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <h3 className="font-display font-bold text-sm flex items-center gap-2 mb-3"><CalendarClock className="h-4 w-4 text-[#008080]" />{t("apanel.upcomingRenewals")}</h3>
          <div className="space-y-1.5">
            {stats.upcoming.map((u) => (
              <div key={u.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5 last:border-0">
                <span className="font-medium truncate">{u.business_name}</span>
                <span className={`text-xs ${u.canceling ? "text-rose-600" : "text-slate-500"}`}>
                  {u.canceling ? t("apanel.deactivatesOn") : t("apanel.renewsOn")} {formatDate(u.end, lang)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex flex-wrap gap-2">
        {["all", "pending", "verified", "rejected"].map((f) => (
          <button key={f} data-testid={`biz-filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f ? "bg-[#008080] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
            {t(`apanel.filter_${f}`)}
          </button>
        ))}
      </div>

      {/* Accounts */}
      {list.length === 0 ? (
        <div className="text-center py-12 text-slate-400">{t("apanel.noBusiness")}</div>
      ) : (
        <div className="space-y-3" data-testid="business-list">
          {list.map((b) => {
            const vs = b.verification_status || "pending";
            return (
              <div key={b.id} data-testid={`biz-row-${b.id}`} className="bg-white border border-slate-200 rounded-2xl p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-display font-bold text-base flex items-center gap-2 flex-wrap">
                      {b.business_name || b.name}
                      {b.is_professional && <BadgeCheck className="h-4 w-4 text-[#008080]" />}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${VS_BADGE[vs]}`}>{t(`apanel.status_${vs}`)}</span>
                      {b.cancel_at_period_end && <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-600">{t("apanel.cancelRequested")}</span>}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">@{b.nickname} · {b.email}</p>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5 mt-2 text-sm text-slate-600">
                      <span><b className="text-slate-400 font-normal">{t("apanel.nif")}:</b> {b.business_nif || "—"}</span>
                      <span><b className="text-slate-400 font-normal">{t("apanel.category")}:</b> {catLabel(b.business_category)}</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-slate-400" />{b.municipality || "—"}{b.island ? `, ${ISLAND_NAME[b.island]}` : ""}</span>
                      {b.phone && <span>{b.phone}</span>}
                      {b.business_address && <span className="sm:col-span-2 text-slate-500">{b.business_address}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-slate-400">{t("apanel.subStatus")}</p>
                    <p className="text-sm font-semibold">{b.subscription_status || "—"}</p>
                    {b.subscription_current_period_end && (
                      <p className="text-[11px] text-slate-500 mt-1">{b.cancel_at_period_end ? t("apanel.deactivatesOn") : t("apanel.renewsOn")} {formatDate(b.subscription_current_period_end, lang)}</p>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
                  {vs !== "verified" && <Button data-testid={`biz-verify-${b.id}`} onClick={() => onVerify(b.id)} className="h-9 bg-emerald-500 hover:bg-emerald-600"><ShieldCheck className="h-4 w-4 mr-1" />{t("apanel.verifyBtn")}</Button>}
                  {vs !== "rejected" && <Button data-testid={`biz-reject-${b.id}`} onClick={() => onReject(b.id)} variant="outline" className="h-9 text-rose-600 border-rose-200 hover:bg-rose-50"><X className="h-4 w-4 mr-1" />{t("apanel.rejectBtn")}</Button>}
                  {b.is_professional
                    ? <Button data-testid={`biz-badge-off-${b.id}`} onClick={() => onToggleBadge(b.id, false)} variant="outline" className="h-9 text-amber-600 border-amber-200 hover:bg-amber-50"><Ban className="h-4 w-4 mr-1" />{t("apanel.revokeBadge")}</Button>
                    : <Button data-testid={`biz-badge-on-${b.id}`} onClick={() => onToggleBadge(b.id, true)} variant="outline" className="h-9 text-[#008080] border-teal-200 hover:bg-teal-50"><BadgeCheck className="h-4 w-4 mr-1" />{t("apanel.restoreBadge")}</Button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
